import React from 'react';
import { Box, Button, Card, CardContent, CardHeader, Container } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

interface ReferralRouteShellProps {
  title?: string;
  backTo: string;
  headerLeft?: React.ReactNode;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fitContent?: boolean;
  children: React.ReactNode;
}

const ReferralRouteShell: React.FC<ReferralRouteShellProps> = ({
  title,
  backTo,
  headerLeft,
  // REQ-026: admin pages can opt into a wider shell while normal referral pages keep md.
  maxWidth = 'md',
  fitContent = false,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateBackTo =
    typeof location.state === 'object' &&
    location.state !== null &&
    'backTo' in location.state &&
    typeof (location.state as { backTo?: unknown }).backTo === 'string'
      ? (location.state as { backTo: string }).backTo
      : null;
  const resolvedBackTo =
    stateBackTo &&
    (stateBackTo.startsWith('/dashboard') || stateBackTo.startsWith('/admins'))
      ? stateBackTo
      : backTo;
  const backButton = (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={() => navigate(resolvedBackTo)}
    >
      Back
    </Button>
  );

  return (
    <Container
      maxWidth={maxWidth}
      data-testid="referral-route-shell-container"
      sx={{
        // REQ-009: mobile referral pages should sit flush without extra shell margin.
        my: { xs: 0, sm: 0.75 },
        px: 0,
        // REQ-026: admin data tables can make the route card grow to their natural width.
        width: fitContent ? 'max-content' : undefined,
        minWidth: fitContent ? '100%' : undefined,
      }}
    >
      <Card
        variant="outlined"
        data-testid="referral-route-shell-card"
        sx={(theme) => ({
          // REQ-009: remove the route-card outline on mobile while keeping the larger-screen shell.
          [theme.breakpoints.down('sm')]: {
            border: 'none',
            borderRadius: 0,
          },
        })}
      >
        {title ? (
          <CardHeader title={title} action={backButton} />
        ) : (
          <Box
            sx={{
              // REQ-009: detail conversation cards should keep only the back control, not a redundant page title.
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              px: 2,
              pt: 2,
              pb: 0.5,
            }}
            data-testid="referral-route-shell-header-row"
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>{headerLeft}</Box>
            {backButton}
          </Box>
        )}
        <CardContent>{children}</CardContent>
      </Card>
    </Container>
  );
};

export default ReferralRouteShell;
